import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Icon } from '@mgc/react-components';
import MapPropTypes from '../../MapPropTypes';
import strings from '../../../strings';
import './SiteList.scss';

export default class SiteList extends Component {
    static propTypes = {
        sites: PropTypes.arrayOf(MapPropTypes.SiteProps),
        favoriteSite: PropTypes.string,
        favoriteServer: PropTypes.string,
        setFavorites: PropTypes.func.isRequired,
        serverAliases: PropTypes.object.isRequired,
        setServerAlias: PropTypes.func.isRequired,
    };

    constructor() {
        super();
        this.state = { aliasBuffers: {} };
    }

    _renderFavoriteButton(site, server) {
        const isFavorite =
            this.props.favoriteSite &&
            this.props.favoriteServer &&
            this.props.favoriteSite === site.id &&
            this.props.favoriteServer === server.id;
        const title = isFavorite
            ? strings.currentFavorite
            : strings.favoriteThisServer;
        const btnClass = isFavorite
            ? 'btn-info text-inverse'
            : 'btn-info-outline';

        return (
            <span>
                <OverlayTrigger
                    key={server.id}
                    placement="left"
                    trigger={['hover', 'focus']}
                    overlay={<Tooltip id={server.id}>{title}</Tooltip>}
                >
                    <button
                        className={`site-favorite-button btn btn-xs ${btnClass}`}
                        onClick={() => this.props.setFavorites(site, server)}
                    >
                        <Icon name="icon-star-full" /> {strings.favorite}
                    </button>
                </OverlayTrigger>
            </span>
        );
    }

    _setEditing(server) {
        this.setState({
            aliasBuffers: {
                ...this.state.aliasBuffers,
                [server.id]:
                    this.props.serverAliases[server.id] || server.mentorId,
            },
        });
    }

    _updateAliasBuffer(server, event) {
        this.setState({
            aliasBuffers: {
                ...this.state.aliasBuffers,
                [server.id]: event.target.value,
            },
        });
    }

    _wipeAliasUpdate(server) {
        const newState = {
            aliasBuffers: { ...this.state.aliasBuffers },
        };
        delete newState.aliasBuffers[server.id];
        this.setState(newState);
    }

    _saveAliasUpdate(server) {
        this.props.setServerAlias(server, this.state.aliasBuffers[server.id]);
        this._wipeAliasUpdate(server);
    }

    _renderStaticServerName(server) {
        const name = this.props.serverAliases[server.id] || (
            <em className="text-muted">{strings.none}</em>
        );
        const clearIcon = this.props.serverAliases[server.id] ? (
            <a
                className="cursor-pointer text-xxs text-danger"
                onClick={() => this.props.setServerAlias(server, '')}
            >
                <Icon name="icon-cross2" />
            </a>
        ) : null;

        return (
            <span>
                <span
                    onDoubleClick={(evt) => {
                        this._setEditing(server);
                        evt.preventDefault();
                    }}
                >
                    {name}
                </span>
                &nbsp;
                {clearIcon}
                &nbsp;
                <a
                    className="cursor-pointer text-xxs text-primary"
                    onClick={() => this._setEditing(server)}
                >
                    <Icon name="icon-pencil" />
                </a>
            </span>
        );
    }

    _handleInputKeyPress(server, event) {
        if (event.key === 'Enter') {
            this._saveAliasUpdate(server);
        }
    }

    _renderInputServerName(server) {
        return (
            <span>
                <input
                    onFocus={(evt) => evt.target.select()}
                    className="form-control server-name-input"
                    onChange={(event) => this._updateAliasBuffer(server, event)}
                    onKeyPress={(event) =>
                        this._handleInputKeyPress(server, event)
                    }
                    value={this.state.aliasBuffers[server.id]}
                />
                &nbsp;
                <a
                    className="text-danger cursor-pointer"
                    onClick={() => this._wipeAliasUpdate(server)}
                >
                    <Icon name="icon-cross2" />
                </a>
                &nbsp;
                <a
                    className="text-success cursor-pointer"
                    onClick={() => this._saveAliasUpdate(server)}
                >
                    <Icon name="icon-checkmark" />
                </a>
            </span>
        );
    }

    _renderNickname(server) {
        if (this.state.aliasBuffers[server.id] !== undefined) {
            return this._renderInputServerName(server);
        }

        return this._renderStaticServerName(server);
    }

    _renderPlatform(server) {
        if (!server.platform) {
            return (
                <em className="text-mute">
                    <Icon name="icon-display" />
                    &nbsp;{strings.unknownPlatform}
                </em>
            );
        }

        let iconName = 'icon-display';
        if (server.platform.mentorId === 'LINUX') {
            iconName = 'icon-tux';
        }
        if (server.platform.mentorId === 'WINDOWS') {
            iconName = 'icon-windows8';
        }

        return (
            <span className="text-mute">
                <Icon name={iconName} />
                &nbsp;
                {server.platform.vendor} - {server.platform.variant}
            </span>
        );
    }

    _renderSite(site, i) {
        return (
            <div className="card text-sm m-a" key={i}>
                <div className="card-header p-a-sm">
                    {strings.serversFor}&nbsp;
                    <b>
                        {site.mentorId} {' - '}
                        {site.name}
                        {site.city ? (
                            <span>
                                , {site.city}, {site.state}
                            </span>
                        ) : (
                            ''
                        )}
                    </b>
                </div>
                <div className="card-block p-a-0">
                    {site.servers.map((server, j) => {
                        return (
                            <div className="row p-a-sm p-r" key={j}>
                                <div className="col-md-9 server-name">
                                    <strong>{strings.server}</strong>:{' '}
                                    {server.mentorId}
                                    <br />                                    
                                    <strong>{strings.nickname}</strong>:{' '}
                                    {this._renderNickname(server)}
                                    <br />
                                    {this._renderPlatform(server)}
                                </div>
                                <div className="col-md-3 text-right p-r-sm">
                                    {this._renderFavoriteButton(site, server)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    _handleDownloadServersBySiteButtonClick() {   
        this.props.downloadServersBySiteList();
    }

    _renderDownloadButton() {
        return (
            <div className="p-t-sm wide-border">
                <div className='col-sm-7'></div>
                <div className='col-sm-5'>
                    <button
                        className="btn btn-info btn-sm"
                        onClick={() =>
                            this._handleDownloadServersBySiteButtonClick()
                        }
                        disabled={false} >
                        <Icon name="icon-download2" />{' '}
                        {strings.ServersBySiteList}
                    </button> 
                </div>
            </div>
        );
    }

    render() {
        const floatCont = {
            border: '3px solid #fff',
            padding: '10px',
            width: '50%',
            position: 'right',
        };
        const floatChild = {
            width: '50%',
            float: 'right',
            padding: '10px',
            // border: '2px solid red',
        };
        return (
            <div className='container'>
                {this._renderDownloadButton()}
                <div className="site-list">
                    {this.props.sites.map((site, i) => this._renderSite(site, i))}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./app/components/Modals/AccountModal/SiteList.jsx